import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useStartConversation } from '@/hooks/useStartConversation'

const USERNAME_REGEX = /^[a-z0-9_.]{4,24}$/

const ERROR_MESSAGES: Record<string, string> = {
  username_not_found: 'No resident found with that username.',
  cannot_chat_with_self: "That's your own username.",
  cannot_start_conversation: 'This conversation is unavailable.',
  username_required: 'Please enter a username.',
}

export function NewChatPage() {
  const navigate = useNavigate()
  const { mutateAsync, isPending } = useStartConversation()
  const [username, setUsername] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Strip leading @ and lowercase
  function normalizeInput(val: string) {
    return val.replace(/^@/, '').toLowerCase()
  }

  const normalized = normalizeInput(username)
  const isValidFormat = USERNAME_REGEX.test(normalized)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isValidFormat) return
    setError(null)

    try {
      const result = await mutateAsync(normalized)
      if (result.conversation_id) {
        navigate(`/chat/${result.conversation_id}`, { replace: true })
      }
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Something went wrong.'
      setError(ERROR_MESSAGES[msg] ?? msg)
    }
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-14 z-30 bg-background border-b">
        <div className="flex items-center gap-3 px-4 h-12">
          <button
            onClick={() => navigate('/chat')}
            className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-accent"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">New Chat</h1>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-6 max-w-md mx-auto">
        <h2 className="text-xl font-semibold mb-1">Start a chat</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Enter a resident's username to start chatting.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="text"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value)
                setError(null)
              }}
              placeholder="@username"
              autoFocus
              className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            {username && !isValidFormat && (
              <p className="text-xs text-muted-foreground mt-1.5">
                4–24 characters: lowercase letters, digits, underscore, or dot.
              </p>
            )}
            {error && (
              <p className="text-xs text-destructive mt-1.5">{error}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={!isValidFormat || isPending}
            className="w-full"
          >
            {isPending ? 'Finding...' : 'Find & Chat'}
          </Button>
        </form>
      </div>
    </div>
  )
}
